import { Box, Button, Stack } from '@mui/material';
import { BrandGroup } from '@provision/entity-types';
import { ReactNode, useCallback, useRef } from 'react';
import {
  DynamicForm,
  FormField,
  FormInfo,
  getFormField,
  MAX_NUMBER_LENGTH,
  MIN_NUMBER_LENGTH,
} from 'src/components/Forms';
import { VoiceChannelSettingFormFields, VoiceFormModel } from './types';

const commonProps = {
  variant: 'outlined',
  minLength: MIN_NUMBER_LENGTH,
  maxLength: MAX_NUMBER_LENGTH,
  textFieldProps: {
    sx: {
      variant: 'standard',
      alignSelf: 'flex-start',
      minWidth: '20rem',
      maxWidth: 'min(25rem, 100%)',
      width: '100%',
      padding: 0,
    },
  },
};

type PhoneExtFieldName =
  | VoiceChannelSettingFormFields.PhonePublishedExt
  | VoiceChannelSettingFormFields.PhoneRerouteExt;

/** Numeric-only extension input rendered beside a phone number field. */
function getPhoneExtField(name: PhoneExtFieldName): FormField {
  return {
    type: 'textfield',
    name,
    label: 'Extension',
    variant: 'outlined',
    textFieldProps: {
      sx: {
        width: '8rem',
        padding: 0,
      },
      inputProps: {
        inputMode: 'numeric',
        pattern: '[0-9]*',
      },
    },
  };
}

/** Lays a phone number field and its extension field out on one row. */
function renderPhoneWithExt(
  extField: FormField,
): (field: FormField, disabled?: boolean) => ReactNode {
  const renderField = (field: FormField, disabled?: boolean) => {
    return (
      <Stack
        key={`${field.name}-with-ext`}
        direction="row"
        gap={1.5}
        alignItems="flex-start"
        sx={{ width: '100%', maxWidth: 'min(34rem, 100%)' }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {getFormField(
            {
              ...field,
              renderField: undefined,
              textFieldProps: {
                ...('textFieldProps' in field ? field.textFieldProps : {}),
                sx: {
                  width: '100%',
                  padding: 0,
                },
              },
            } as FormField,
            disabled,
          )}
        </Box>
        <Box sx={{ flex: '0 0 auto' }}>{getFormField(extField, disabled)}</Box>
      </Stack>
    );
  };

  return renderField;
}

export const ChannelsVoiceForm = ({
  handleSave,
  selectedBrandGroup,
}: {
  handleSave: (data: VoiceFormModel) => void;
  selectedBrandGroup: BrandGroup | null;
}) => {
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);

  const triggerSubmit = useCallback(() => {
    submitButtonRef?.current?.click();
  }, []);

  const handleOnSubmit = useCallback(
    (values: VoiceFormModel, formInfo?: FormInfo<VoiceFormModel>) => {
      const baselineValues = getFormValues(selectedBrandGroup);

      const changedField = Object.keys(values).find((key) => {
        const field = key as VoiceChannelSettingFormFields;
        const currentValue = values[field] ?? '';
        const baselineValue = baselineValues[field] ?? '';

        return field in values && currentValue !== baselineValue;
      });

      const { formState } = formInfo || {};
      const isValid = !Object.keys(formState?.errors ?? {}).length;

      if (changedField && isValid) {
        handleSave(values);
      }
    },
    [selectedBrandGroup, handleSave],
  );

  const fields = [
    {
      type: 'phone',
      name: VoiceChannelSettingFormFields.PhoneSupport,
      label: 'Client Support Number',
      helperText:
        'Main inbound support number for client calls. Also used for outbound caller ID and client-facing support.',
      ...commonProps,
    },
    {
      type: 'phone',
      name: VoiceChannelSettingFormFields.PhoneSignature,
      label: 'Client Signature Number',
      helperText:
        'Legacy number for Signature clients. No longer required because routing uses site lookup.',
      ...commonProps,
    },
    {
      type: 'phone',
      name: VoiceChannelSettingFormFields.PhoneTwilio,
      label: 'Twilio Forwarding Number (if applicable)',
      helperText:
        'Number used for Twilio call forwarding services. Leave blank if not using Twilio.',
      ...commonProps,
    },
    {
      type: 'phone',
      name: VoiceChannelSettingFormFields.CallerIdPhone,
      label: 'Outbound Caller ID',
      helperText: 'Number shown when making outbound calls. Uses Client Support Number.',
      ...commonProps,
    },
    {
      type: 'phone',
      name: VoiceChannelSettingFormFields.PhoneReroute,
      label: 'Return Routing Number',
      helperText:
        'Number used for return call routing in the phone system. The extension is dialed after the call is answered.',
      ...commonProps,
      renderField: renderPhoneWithExt(
        getPhoneExtField(VoiceChannelSettingFormFields.PhoneRerouteExt),
      ),
    },
    {
      type: 'phone',
      name: VoiceChannelSettingFormFields.PhonePublished,
      label: 'Client Facing Support Number',
      helperText: 'Number displayed to clients for support. Uses Client Support Number.',
      ...commonProps,
      renderField: renderPhoneWithExt(
        getPhoneExtField(VoiceChannelSettingFormFields.PhonePublishedExt),
      ),
    },
  ] as FormField[];

  return (
    <DynamicForm
      watchable
      watchableSubmitOnlyChangedField
      onSubmit={handleOnSubmit}
      handleBlur={triggerSubmit}
      fields={fields}
      style={{ gap: '1.5rem' }}
      value={getFormValues(selectedBrandGroup)}
      options={{
        mode: 'all',
      }}
    >
      <Button
        type="submit"
        ref={submitButtonRef}
        sx={{
          display: 'none',
        }}
      >
        Save
      </Button>
    </DynamicForm>
  );
};

function getFormValues(brandGroup: BrandGroup | null) {
  const formatPhoneValue = (value?: number | string | null) =>
    value === null || value === undefined ? '' : value.toString();
  const callerIdValue = formatPhoneValue(brandGroup?.callerIdPhone);
  const formValues: VoiceFormModel = {
    [VoiceChannelSettingFormFields.Id]: brandGroup?.id ?? '',
    [VoiceChannelSettingFormFields.PhoneSupport]: formatPhoneValue(
      brandGroup?.fwdPhoneSupport,
    ),
    [VoiceChannelSettingFormFields.PhoneSignature]: formatPhoneValue(
      brandGroup?.fwdPhoneSignature,
    ),
    [VoiceChannelSettingFormFields.PhoneTwilio]: formatPhoneValue(brandGroup?.fwdPhoneTwilio),
    [VoiceChannelSettingFormFields.CallerIdPhone]: callerIdValue
      ? callerIdValue.replace(/^1/, '')
      : '',
    [VoiceChannelSettingFormFields.PhoneReroute]: formatPhoneValue(
      brandGroup?.fwdPhoneReroute,
    ),
    [VoiceChannelSettingFormFields.PhoneRerouteExt]: formatPhoneValue(
      brandGroup?.fwdPhoneRerouteExt,
    ),
    [VoiceChannelSettingFormFields.PhonePublished]: formatPhoneValue(
      brandGroup?.fwdPhonePublished,
    ),
    [VoiceChannelSettingFormFields.PhonePublishedExt]: formatPhoneValue(
      brandGroup?.fwdPhonePublishedExt,
    ),
  };

  return formValues;
}
